import React, { useEffect, useState } from 'react';
import { requestFcmToken, onMessageListener } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';

const NotificationManager = () => {
    const { user, isAuthenticated } = useAuth();
    const [notification, setNotification] = useState({ title: '', body: '' });
    const [permissionRequested, setPermissionRequested] = useState(false);

    // 1. CRITICAL FIX: Request notification AFTER login, not on mount
    useEffect(() => {
        if (isAuthenticated && user && !permissionRequested) {
            // Trigger FCM setup when user authenticates
            requestNotificationAndSync();
        }
    }, [isAuthenticated, user]);

    // 2. Listen for foreground messages
    useEffect(() => {
        onMessageListener()
            .then((payload) => {
                if (!payload) return;

                const data = payload.data || {};
                const title = data.title || 'New Notification';
                const body = data.body || data.message || '';
                const notificationId = data.notificationId;

                // Send ACK if background didn't catch it
                if (notificationId) {
                    sendDeliveryAck(notificationId);
                }

                toast.info(
                    <div>
                        <h4 className="font-bold text-sm">{title}</h4>
                        <p className="text-xs">{body}</p>
                    </div>,
                    {
                        autoClose: 6000,
                        pauseOnHover: true,
                        style: { borderLeft: '4px solid #e11d48' } // Brand color
                    }
                );

                if (Notification.permission === 'granted' && document.hidden) {
                    new Notification(title, {
                        body: body,
                        icon: data.icon || '/notification-icon.png',
                        tag: data.type || 'default',
                        data: { url: data.url, notificationId }
                    });
                }
            })
            .catch(err => console.log('foreground listen error: ', err));
    }, []);

    const sendDeliveryAck = async (notificationId) => {
        try {
            await api.post('/admin/notifications/delivered', { notificationId });
            console.log('✅ Delivery ACK sent for:', notificationId);
        } catch (error) {
            console.error('❌ Failed to send delivery ACK:', error);
        }
    };

    const requestNotificationAndSync = async () => {
        if (!('Notification' in window)) {
            console.warn('⚠️ Notifications not supported in this browser');
            return;
        }

        try {
            setPermissionRequested(true);

            // Check current permission
            let permission = Notification.permission;

            // If default (not yet decided), request permission
            if (permission === 'default') {
                console.log('📱 Requesting notification permission...');
                permission = await Notification.requestPermission();
            }

            if (permission === 'granted') {
                console.log('✅ Notification permission granted');
                const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

                if (!vapidKey) {
                    console.error('❌ VAPID key missing in environment');
                    return;
                }

                // CRITICAL FIX: Force fresh token generation on every login
                const token = await requestFcmToken(vapidKey);

                if (token) {
                    const oldToken = localStorage.getItem('fcmToken');

                    // Save new token to localStorage
                    localStorage.setItem('fcmToken', token);
                    localStorage.setItem('fcmTokenUpdatedAt', new Date().toISOString());

                    console.log('✅ FCM Token generated:', token.substring(0, 20) + '...');

                    // Sync to backend immediately
                    await saveTokenToBackend(token);
                } else {
                    console.error('❌ Failed to get FCM token');
                }
            } else if (permission === 'denied') {
                console.warn('⚠️ Notification permission denied by user');
            } else {
                console.warn('⚠️ Notification permission:', permission);
            }
        } catch (error) {
            console.error('❌ Error in FCM setup:', error);
        }
    };

    const getDeviceId = () => {
        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
            // Create unique device ID with timestamp
            deviceId = 'web_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
            localStorage.setItem('deviceId', deviceId);
            console.log('📱 Generated new deviceId:', deviceId);
        }
        return deviceId;
    };

    const saveTokenToBackend = async (token) => {
        try {
            await api.post('/user/fcm-token', {
                fcmToken: token,
                deviceId: getDeviceId()
            });
            console.log('✅ FCM Token synchronized with backend');
        } catch (error) {
            // Silently fail if unauthorized, but log others
            if (error.response?.status !== 401) {
                console.error('❌ Failed to sync FCM token:', error);
            }
        }
    };

    return null;
};

export default NotificationManager;
